import React, { useId, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFocusTrap } from '../../hooks/use-focus-trap';
import { createCheckoutSession, PlanType } from '../../services/paymentService';
import './UpgradeModal.scss';

// Backend caps a checkout at 50 seats (CreateCheckoutSessionRequest.seat_count). The buyer
// takes one of them, so the teacher picker tops out one lower.
const MAX_SCHOOL_TEACHERS = 49;

interface UpgradeModalProps {
  onClose?: () => void;
}

const TEACHER_PRICE_MONTHLY = 15;
const TEACHER_PRICE_ANNUAL = 9;
const SCHOOL_PRICE_PER_SEAT_MONTHLY = 15;
const SCHOOL_PRICE_PER_SEAT_ANNUAL = 9;

const UpgradeModal: React.FC<UpgradeModalProps> = ({ onClose }) => {
  const { trialSessionsUsed, trialSessionLimit, currentUser, token } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState<'teacher' | 'school' | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [schoolSeatsInput, setSchoolSeatsInput] = useState('1');
  const schoolSeats = Math.min(MAX_SCHOOL_TEACHERS, Math.max(1, parseInt(schoolSeatsInput, 10) || 1));
  const seatInputId = useId();
  const seatPreviewId = useId();
  const modalRef = useRef<HTMLDivElement>(null);
  // Mounted only while open. SideBar also closes this on Escape; a second onClose call is a
  // no-op (it only sets the parent's "open" state to false).
  useFocusTrap(modalRef, true, { onEscape: onClose });

  const trialEndDate = currentUser?.access_end_date
    ? new Date(currentUser.access_end_date).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const isDateExpired =
    currentUser?.access_end_date && new Date() > new Date(currentUser.access_end_date);
  const isLimitReached = trialSessionsUsed >= trialSessionLimit;
  const isTrialEnded = isDateExpired || isLimitReached;

  const teacherPrice = isAnnual ? TEACHER_PRICE_ANNUAL : TEACHER_PRICE_MONTHLY;
  const schoolPricePerSeat = isAnnual ? SCHOOL_PRICE_PER_SEAT_ANNUAL : SCHOOL_PRICE_PER_SEAT_MONTHLY;
  // +1 for the buyer themselves; schoolSeats is the number of invited teachers
  const schoolTotalSeats = schoolSeats + 1;
  const schoolTotal = schoolTotalSeats * schoolPricePerSeat;
  const teacherAnnualTotal = teacherPrice * 12;
  const schoolAnnualTotal = schoolTotal * 12;

  const handleTeacherPlan = async (planType: PlanType) => {
    if (!token) return;
    setCheckoutError(null);
    setCheckoutLoading('teacher');
    try {
      const { checkout_url } = await createCheckoutSession(token, planType);
      window.location.href = checkout_url;
    } catch (err: any) {
      setCheckoutError(err.message ?? 'Something went wrong. Please try again.');
      setCheckoutLoading(null);
    }
  };

  const handleSchoolPlan = async (planType: PlanType) => {
    if (!token) return;
    setCheckoutError(null);
    setCheckoutLoading('school');
    try {
      const { checkout_url } = await createCheckoutSession(token, planType, schoolTotalSeats);
      window.location.href = checkout_url;
    } catch (err: any) {
      setCheckoutError(err.message ?? 'Something went wrong. Please try again.');
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="upgrade-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="upgrade-modal-title">
      <div className="upgrade-modal" ref={modalRef}>

        {onClose && (
          <button className="upgrade-modal__close" onClick={onClose} aria-label="Close">✕</button>
        )}

        {/* Header */}
        <div className="upgrade-modal__header">
          <div className="upgrade-modal__top-badge">Reality Coach Pricing</div>
          <h2 id="upgrade-modal-title" className="upgrade-modal__title">
            {isTrialEnded
              ? (currentUser?.is_trial ? 'Your free trial has ended' : 'Your subscription has expired')
              : 'Choose the option that best fits your school'}
          </h2>
          <p className="upgrade-modal__subtitle">
            {isTrialEnded
              ? (currentUser?.is_trial
                  ? (isLimitReached
                      ? `You've used all ${trialSessionLimit} trial sessions. Subscribe to keep practicing with Reality Coach.`
                      : `Your trial expired on ${trialEndDate}. Subscribe to keep practicing with Reality Coach.`)
                  : `Your subscription expired on ${trialEndDate}. Renew your plan to keep practicing with Reality Coach.`)
              : 'Compare an individual teacher plan, a school purchase, and a district option. Toggle updates pricing. Only School uses a seat selector.'}
          </p>

          {/* Billing toggle */}
          <div className="upgrade-modal__toggle">
            <span className={!isAnnual ? 'upgrade-modal__toggle-label--active' : 'upgrade-modal__toggle-label'}>
              Monthly view
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={isAnnual}
              className={`upgrade-modal__toggle-btn${isAnnual ? ' upgrade-modal__toggle-btn--on' : ''}`}
              onClick={() => setIsAnnual(v => !v)}
              aria-label="Annual billing"
            >
              <span className="upgrade-modal__toggle-knob" />
            </button>
            <span className={isAnnual ? 'upgrade-modal__toggle-label--active' : 'upgrade-modal__toggle-label'}>
              Annual view
            </span>
            {isAnnual && <span className="upgrade-modal__discount-badge">40% Discount</span>}
          </div>
        </div>

        {/* Pricing cards */}
        <div className="upgrade-modal__cards">

          {/* Google Marketplace */}
          <div className="upgrade-modal__card">
            <h3 className="upgrade-modal__card-name">Google Marketplace</h3>
            <div className="upgrade-modal__card-price">
              <span className="upgrade-modal__price-amount upgrade-modal__price-amount--talk">GCP Billing</span>
            </div>
            <div className="upgrade-modal__card-billing">
              via your existing GCP account
            </div>
            <a
              className="upgrade-modal__card-cta upgrade-modal__card-cta--outline"
              href="https://console.cloud.google.com/marketplace/product/da-realitycoach-prod/reality-coach"
              target="_blank"
              rel="noopener noreferrer"
            >
              Continue with Google Marketplace
            </a>
            <p className="upgrade-modal__card-tagline">
              Subscribe via your existing GCP billing account with no separate payment method needed.
            </p>
            <hr className="upgrade-modal__card-divider" />
            <ul className="upgrade-modal__card-features">
              <li>GCP billing account</li>
              <li>Full platform access</li>
              <li>Managed via Cloud Console</li>
              <li>Usage-based billing</li>
            </ul>
          </div>

          {/* Teacher */}
          <div className="upgrade-modal__card">
            <h3 className="upgrade-modal__card-name">Teacher</h3>
            <div className="upgrade-modal__card-price">
              <span className="upgrade-modal__price-amount">${isAnnual ? teacherAnnualTotal : teacherPrice}</span>
            </div>
            <div className="upgrade-modal__card-billing">
              per teacher / {isAnnual ? 'year, billed annually' : 'month, billed monthly'}
            </div>
            {isAnnual && (
              <p className="upgrade-modal__price-subtext">
                ${teacherPrice} / month | ${teacherAnnualTotal} / year
              </p>
            )}
            <button
              className="upgrade-modal__card-cta upgrade-modal__card-cta--outline"
              onClick={() => handleTeacherPlan(isAnnual ? 'yearly' : 'monthly')}
              disabled={checkoutLoading !== null}
            >
              {checkoutLoading === 'teacher' ? 'Redirecting…' : 'Choose teacher'}
            </button>
            <p className="upgrade-modal__card-tagline">
              Individual access for one teacher or staff member practicing real student conversations.
            </p>
            <hr className="upgrade-modal__card-divider" />
            <ul className="upgrade-modal__card-features">
              <li>Unlimited roleplays</li>
              <li>Post-session feedback</li>
              <li>Personal analytics dashboard</li>
              <li>Badges, streaks, and progress milestones</li>
            </ul>
          </div>

          {/* School — Recommended */}
          <div className="upgrade-modal__card upgrade-modal__card--recommended">
            <div className="upgrade-modal__recommended-tag">RECOMMENDED</div>
            <h3 className="upgrade-modal__card-name">School</h3>
            <div className="upgrade-modal__card-price">
              <span className="upgrade-modal__price-amount">${isAnnual ? schoolAnnualTotal : schoolTotal}</span>
            </div>
            <div className="upgrade-modal__card-billing">
              {schoolTotalSeats} seats (1 buyer + {schoolSeats} teacher{schoolSeats !== 1 ? 's' : ''}) / {isAnnual ? 'year, billed annually' : 'month, billed monthly'}
            </div>
            {isAnnual && (
              <p className="upgrade-modal__price-subtext">
                ${schoolTotal} / month | ${schoolAnnualTotal} / year
              </p>
            )}
            <button
              className="upgrade-modal__card-cta upgrade-modal__card-cta--filled"
              onClick={() => handleSchoolPlan(isAnnual ? 'yearly' : 'monthly')}
              disabled={checkoutLoading !== null}
            >
              {checkoutLoading === 'school' ? 'Redirecting…' : 'Choose school'}
            </button>
            <p className="upgrade-modal__card-tagline">
              School purchase for 1 to 50 teachers with shared oversight for implementation and review.
            </p>
            <div className="upgrade-modal__seat-selector">
              <label className="upgrade-modal__seat-label" htmlFor={seatInputId}>Teachers</label>
              <input
                id={seatInputId}
                aria-describedby={seatPreviewId}
                type="number"
                className="upgrade-modal__seat-input"
                value={schoolSeatsInput}
                min={1}
                max={MAX_SCHOOL_TEACHERS}
                onChange={e => { const v = e.target.value; if (v === '' || /^\d+$/.test(v)) setSchoolSeatsInput(v); }}
                onBlur={() => setSchoolSeatsInput(String(schoolSeats))}
              />
              <p className="upgrade-modal__seat-preview" id={seatPreviewId}>
                {schoolTotalSeats} seats total (1 buyer + {schoolSeats} teacher{schoolSeats !== 1 ? 's' : ''}) × ${schoolPricePerSeat}
              </p>
            </div>
            <hr className="upgrade-modal__card-divider" />
            <ul className="upgrade-modal__card-features">
              <li>Everything in Teacher</li>
              <li>School dashboards and reporting</li>
              <li>User management with role-based access</li>
              <li>Organization-level analytics and oversight</li>
            </ul>
          </div>

          {/* District */}
          <div className="upgrade-modal__card">
            <h3 className="upgrade-modal__card-name">District</h3>
            <div className="upgrade-modal__card-price">
              <span className="upgrade-modal__price-amount upgrade-modal__price-amount--talk">Let's talk</span>
            </div>
            <div className="upgrade-modal__card-billing">
              custom quote<br />district purchasing and rollout
            </div>
            <a
              className="upgrade-modal__card-cta upgrade-modal__card-cta--outline"
              href="mailto:sales@realitycoach.ai"
            >
              Contact sales
            </a>
            <p className="upgrade-modal__card-tagline">
              Districtwide purchasing with the same platform capabilities as Teacher and School.
            </p>
            <hr className="upgrade-modal__card-divider" />
            <ul className="upgrade-modal__card-features">
              <li>Everything in Teacher and School</li>
              <li>District dashboards and reporting</li>
              <li>Administrator tools and user management</li>
              <li>Custom quote through sales</li>
            </ul>
          </div>

        </div>

        {checkoutError && (
          <p className="upgrade-modal__error" role="alert">{checkoutError}</p>
        )}

        {onClose && !isTrialEnded && (
          <div className="upgrade-modal__footer">
            <button className="upgrade-modal__dismiss" onClick={onClose}>
              I'll decide later
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default UpgradeModal;
